import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { UserPlus, Eye, ShoppingCart } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { OrderGateway } from '../../gateways/OrderGateway';
import { AnalyticsGateway } from '../../gateways/AnalyticsGateway';

const colors = ['#111827', '#577CC6'];

const SiteAnalytics: React.FC = () => {
    const [visitsFilter, setVisitsFilter] = useState<'hourly' | 'daily' | 'monthly'>('monthly');
    const [visitsTrendToggle, setVisitsTrendToggle] = useState<'actual' | 'trend'>('actual');
    const [effectiveDate] = useState(new Date());

    // Registration filters and toggles
    const [registrationFilter, setRegistrationFilter] = useState<'hourly' | 'daily' | 'monthly'>('monthly');
    const [registrationTrendToggle, setRegistrationTrendToggle] = useState<'actual' | 'trend'>('actual');

    // Customer distribution widget state
    const [customerStats, setCustomerStats] = useState({ newCustomers: 0, returningCustomers: 0, newChange: 0, returningChange: 0, total: 0 });
    
    // Add state for real visits data
    const [visitsData, setVisitsData] = useState<{ hourly: any[], daily: any[], monthly: any[] } | null>(null);
    const [totalVisits, setTotalVisits] = useState(0);
    const [visitsLoading, setVisitsLoading] = useState(true);
    const [previousWeekVisits, setPreviousWeekVisits] = useState(0);

    // Add state for real registrations data
    const [registrationsData, setRegistrationsData] = useState<{ hourly: any[], daily: any[], monthly: any[] } | null>(null);
    const [totalRegistrations, setTotalRegistrations] = useState(0);
    const [registrationsLoading, setRegistrationsLoading] = useState(true);
    const [previousWeekRegistrations, setPreviousWeekRegistrations] = useState(0);

    // Add state for checkout completion data
    const [checkoutData, setCheckoutData] = useState({ completed: 0, incomplete: 0, completedChange: 0, incompleteChange: 0 });
    const [checkoutLoading, setCheckoutLoading] = useState(true);

    useEffect(() => {
        const fetchVisitsData = async () => {
            setVisitsLoading(true);
            const data = await AnalyticsGateway.GetSiteVisits();
            
            if (data && data.daily) {                
                
                // Process daily data into chart formats
                const dailyEntries = Object.entries(data.daily)
                    .map(([date, count]) => ({
                        date: new Date(date),
                        dateStr: date,
                        count: count as number
                    }))
                    .sort((a, b) => a.date.getTime() - b.date.getTime());

                // Calculate previous week visits for comparison
                const sevenDaysAgo = new Date(effectiveDate);
                sevenDaysAgo.setDate(effectiveDate.getDate() - 7);
                const fourteenDaysAgo = new Date(effectiveDate);
                fourteenDaysAgo.setDate(effectiveDate.getDate() - 14);
                
                const currentWeekVisits = dailyEntries
                    .filter(entry => entry.date >= sevenDaysAgo && entry.date <= effectiveDate)
                    .reduce((sum, entry) => sum + entry.count, 0);
                
                const prevWeekVisits = dailyEntries
                    .filter(entry => entry.date >= fourteenDaysAgo && entry.date < sevenDaysAgo)
                    .reduce((sum, entry) => sum + entry.count, 0);
                
                setPreviousWeekVisits(prevWeekVisits);
                setTotalVisits(currentWeekVisits);

                // Generate daily view (last 7 days)
                const daily = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName, i) => {
                    const date = new Date(effectiveDate);
                    date.setDate(effectiveDate.getDate() - (6 - i));
                    const dateStr = date.toISOString().split('T')[0];
                    const visits = data.daily?.[dateStr] || 0;
                    return { name: dayName, visits };
                });

                // Generate monthly view (last 12 months)
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthly = Array.from({ length: 12 }, (_, i) => {
                    const date = new Date(effectiveDate);
                    date.setMonth(effectiveDate.getMonth() - (11 - i));
                    const monthName = months[date.getMonth()];
                    
                    // Sum all days in this month
                    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
                    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                    
                    let visits = 0;
                    for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
                        const dateStr = d.toISOString().split('T')[0];
                        visits += data.daily?.[dateStr] || 0;
                    }
                    
                    return { name: monthName, visits };
                });

                // Generate hourly view (last 24 hours)
                const hourly = Array.from({ length: 24 }, (_, i) => {
                    const hour = i % 12 === 0 ? 12 : i % 12;
                    const period = i < 12 ? 'AM' : 'PM';
                    
                    // Get actual hourly data for today
                    const today = effectiveDate.toISOString().split('T')[0];
                    const visits = data.hourly?.[today]?.[i] || 0;
                    
                    return { name: `${hour} ${period}`, visits };
                });

                setVisitsData({ hourly, daily, monthly });
            }
            setVisitsLoading(false);
        };

        const fetchRegistrationsData = async () => {
            setRegistrationsLoading(true);
            const data = await AnalyticsGateway.GetRegistrations();
            
            if (data && data.daily) {                
                
                // Process daily data into chart formats
                const dailyEntries = Object.entries(data.daily)
                    .map(([date, count]) => ({
                        date: new Date(date),
                        dateStr: date,
                        count: count as number
                    }))
                    .sort((a, b) => a.date.getTime() - b.date.getTime());

                // Calculate previous week registrations for comparison
                const sevenDaysAgo = new Date(effectiveDate);
                sevenDaysAgo.setDate(effectiveDate.getDate() - 7);
                const fourteenDaysAgo = new Date(effectiveDate);
                fourteenDaysAgo.setDate(effectiveDate.getDate() - 14);
                
                const currentWeekRegs = dailyEntries
                    .filter(entry => entry.date >= sevenDaysAgo && entry.date <= effectiveDate)
                    .reduce((sum, entry) => sum + entry.count, 0);
                
                const prevWeekRegs = dailyEntries
                    .filter(entry => entry.date >= fourteenDaysAgo && entry.date < sevenDaysAgo)
                    .reduce((sum, entry) => sum + entry.count, 0);
                
                setTotalRegistrations(currentWeekRegs);
                setPreviousWeekRegistrations(prevWeekRegs);

                // Generate daily view (last 7 days)
                const daily = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName, i) => {
                    const date = new Date(effectiveDate);
                    date.setDate(effectiveDate.getDate() - (6 - i));
                    const dateStr = date.toISOString().split('T')[0];
                    const registrations = data.daily?.[dateStr] || 0;
                    return { name: dayName, registrations };
                });

                // Generate monthly view (last 12 months)
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthly = Array.from({ length: 12 }, (_, i) => {
                    const date = new Date(effectiveDate);
                    date.setMonth(effectiveDate.getMonth() - (11 - i));
                    const monthName = months[date.getMonth()];
                    
                    // Sum all days in this month
                    const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
                    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                    
                    let registrations = 0;
                    for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
                        const dateStr = d.toISOString().split('T')[0];
                        registrations += data.daily?.[dateStr] || 0;
                    }
                    
                    return { name: monthName, registrations };
                });

                // Generate hourly view (last 24 hours)
                const hourly = Array.from({ length: 24 }, (_, i) => {
                    const hour = i % 12 === 0 ? 12 : i % 12;
                    const period = i < 12 ? 'AM' : 'PM';
                    
                    // Get actual hourly data for today
                    const today = effectiveDate.toISOString().split('T')[0];
                    const registrations = data.hourly?.[today]?.[i] || 0;
                    
                    return { name: `${hour} ${period}`, registrations };
                });

                setRegistrationsData({ hourly, daily, monthly });
            }
            setRegistrationsLoading(false);
        };

        const fetchCheckoutCompletion = async () => {
            setCheckoutLoading(true);
            const data = await AnalyticsGateway.GetCheckoutCompletion(effectiveDate);
            
            const completedChange = data.previousCompleted > 0 
                ? Math.round(((data.completed - data.previousCompleted) / data.previousCompleted) * 100)
                : (data.completed > 0 ? 100 : 0);
            
            const incompleteChange = data.previousIncomplete > 0 
                ? Math.round(((data.incomplete - data.previousIncomplete) / data.previousIncomplete) * 100)
                : (data.incomplete > 0 ? 100 : 0);
            
            setCheckoutData({
                completed: data.completed,
                incomplete: data.incomplete,
                completedChange,
                incompleteChange
            });
            setCheckoutLoading(false);
        };

        fetchVisitsData();
        fetchRegistrationsData();
        fetchCheckoutCompletion();
    }, [effectiveDate]);

    useEffect(() => {
        const fetchOrders = async () => {
            const orders = await OrderGateway.GetOrdersForAnalytics(effectiveDate);

            // Customer distribution calculation
            const customerOrderCounts = new Map<string, number>();
            const allCustomerOrderCounts = new Map<string, number>();
            const prevMonthCustomerOrderCounts = new Map<string, number>();
            const currentMonthFilterDate = new Date(effectiveDate.toDateString());
            currentMonthFilterDate.setDate(1);
            const previousMonthFilterDate = new Date(effectiveDate.toDateString());
            previousMonthFilterDate.setMonth(effectiveDate.getMonth() - 1);
            previousMonthFilterDate.setDate(1);

            // ...existing widget stats...
            const yesterday = new Date(effectiveDate);
            yesterday.setDate(effectiveDate.getDate() - 1);
            const lastMonth = new Date(effectiveDate);
            lastMonth.setMonth(effectiveDate.getMonth() - 1);

            const dailyFilterDate = new Date(effectiveDate.toDateString());
            dailyFilterDate.setDate(effectiveDate.getDate() - 6);
            const monthlyFilterDate = new Date(effectiveDate.toDateString());
            monthlyFilterDate.setMonth(effectiveDate.getMonth() - 11);
            monthlyFilterDate.setDate(1);

            orders.forEach(({ order }) => {
                const createdAt = new Date(order.createdAt);
                //const total = parseFloat(order.totalAmount);
                // Customer distribution logic
                const customerKey = order.email || order.userId || order.phoneNumber || '';
                allCustomerOrderCounts.set(customerKey, (allCustomerOrderCounts.get(customerKey) || 0) + 1);
                if (createdAt >= currentMonthFilterDate) {
                    customerOrderCounts.set(customerKey, (customerOrderCounts.get(customerKey) || 0) + 1);
                } else if (createdAt >= previousMonthFilterDate && createdAt < currentMonthFilterDate) {
                    prevMonthCustomerOrderCounts.set(customerKey, (prevMonthCustomerOrderCounts.get(customerKey) || 0) + 1);
                }                
            });

            // Compute new/returning customers for current month
            let newCustomers = 0, returningCustomers = 0;
            customerOrderCounts.forEach((count, key) => {
                if (count === 1 && allCustomerOrderCounts.get(key) === 1) newCustomers++;
                else returningCustomers++;
            });
            // Compute previous month new/returning
            let prevNewCustomers = 0, prevReturningCustomers = 0;
            prevMonthCustomerOrderCounts.forEach((count, key) => {
                const allCount = allCustomerOrderCounts.get(key) || 0;
                const currentCount = customerOrderCounts.get(key) || 0;
                if (count === 1 && (allCount - currentCount) === 1) prevNewCustomers++;
                else prevReturningCustomers++;
            });
            const percent = (curr: number, prev: number) => prev === 0 ? 100 : Math.round(((curr - prev) / prev) * 100);
            setCustomerStats({
                newCustomers,
                returningCustomers,
                newChange: percent(newCustomers, prevNewCustomers),
                returningChange: percent(returningCustomers, prevReturningCustomers),
                total: newCustomers + returningCustomers
            });
        };

        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveDate]);    

    // Calculate visits change percentage
    const visitsChange = previousWeekVisits > 0 
        ? Math.round(((totalVisits - previousWeekVisits) / previousWeekVisits) * 100)
        : 100;

    // Calculate registrations change percentage
    const registrationsChange = previousWeekRegistrations > 0 
        ? Math.round(((totalRegistrations - previousWeekRegistrations) / previousWeekRegistrations) * 100)
        : 100;

    return (
        <Container className="main-container p-3">
            <h2 className="mb-4">Site Analytics</h2>
            {/* Top Widgets */}
            <Row className="mb-4">
                {[{
                    title: 'Site Visits',
                    value: visitsLoading ? '...' : totalVisits.toLocaleString(),
                    change: visitsChange,
                    icon: <Eye size={20} color="white" />,
                    comparison: 'vs. last week',
                }, {
                    title: 'Incomplete Checkouts',
                    value: checkoutLoading ? '...' : checkoutData.incomplete.toLocaleString(),
                    change: checkoutData.incompleteChange,
                    icon: <ShoppingCart size={20} color="white" />,
                    comparison: 'vs. last month',
                }, {
                    title: 'Registrations',
                    value: registrationsLoading ? '...' : totalRegistrations.toLocaleString(),
                    change: registrationsChange,
                    icon: <UserPlus size={20} color="white" />,
                    comparison: 'vs. last week',
                }].map((widget, idx) => (
                    <Col key={idx} xs={12} sm={6} md={4} className="mb-3">
                        <Card className="shadow border-0">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <Card.Title className="mb-2">{widget.title}</Card.Title>
                                        <h4 className="mb-1">{widget.value}</h4>
                                        <div className={`${widget.change >= 0 ? 'text-success' : 'text-danger'} fw-medium`}>
                                            {`${widget.change > 0 ? '+' : ''}${widget.change}%`} <span className="text-muted">({widget.comparison})</span>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
                                            borderRadius: "0.5rem",
                                            padding: "0.5rem",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {widget.icon}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Site Visits Trend & Customer Distribution */}
            <Row className="mb-4">
                {/* Site Visits Trend - 2/3 width */}
                <Col xs={12} md={8} className="mb-3">
                    <Card className="shadow border-0">
                        <Card.Body>
                            <Card.Title>Site Visits Trend</Card.Title>
                            <div className="text-muted mb-3">Visits over time</div>
                            <div className="mb-3 d-flex gap-2">
                                <div className="btn-group me-2">
                                    {['hourly', 'daily', 'monthly'].map((filter) => (
                                        <button
                                            key={filter}
                                            className={`btn btn-${visitsFilter === filter ? 'primary' : 'outline-primary'}`}
                                            onClick={() => setVisitsFilter(filter as any)}
                                        >
                                            {filter.charAt(0).toUpperCase() + filter.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                <div className="btn-group">
                                    {['actual', 'trend'].map((type) => (
                                        <button
                                            key={type}
                                            className={`btn btn-${visitsTrendToggle === type ? 'primary' : 'outline-primary'}`}
                                            onClick={() => setVisitsTrendToggle(type as 'actual' | 'trend')}
                                        >
                                            {type === 'trend' ? 'Trend' : 'Actual'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {visitsLoading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={visitsData?.[visitsFilter] || []}>
                                        <defs>
                                            <linearGradient id="visitsGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={colors[1]} stopOpacity={0.8} />
                                                <stop offset="100%" stopColor={colors[1]} stopOpacity={0.3} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="visits" fill="url(#visitsGradient)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Customer Distribution - 1/3 width */}
                <Col xs={12} md={4}>
                    <Card className="shadow border-0">
                        <Card.Body>
                            <Card.Title>Customer Distribution
                                <span className="text-muted" style={{ fontSize: '0.9rem', marginLeft: '10px' }}>
                                    {effectiveDate.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}
                                </span>
                            </Card.Title>
                            <div className="text-muted mb-3">New vs returning customers this month</div>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={[
                                            { name: 'New Customers', value: customerStats.newCustomers },
                                            { name: 'Returning Customers', value: customerStats.returningCustomers }
                                        ]}
                                        dataKey="value"
                                        innerRadius={60}
                                        outerRadius={80}
                                        labelLine={false}
                                    >
                                        <Cell fill={colors[1]} /> {/* New Customers - Blue */}
                                        <Cell fill={colors[0]} /> {/* Returning Customers - Purple */}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>

                            {/* Legends with totals and change */}
                            <div className="d-flex justify-content-between mb-3">
                                {[{
                                    name: 'New Customers',
                                    color: colors[1],
                                    value: customerStats.newCustomers,
                                    change: customerStats.newChange,
                                }, {
                                    name: 'Returning Customers',
                                    color: colors[0],
                                    value: customerStats.returningCustomers,
                                    change: customerStats.returningChange,
                                }].map((legend, i) => (
                                    <div
                                        key={i}
                                        className="px-3 py-2 text-center"
                                        style={{
                                            backgroundColor: '#F3F4F6',
                                            borderRadius: '0.5rem',
                                            width: '48%',
                                        }}
                                    >
                                        <div className="d-flex justify-content-center align-items-center mb-1">
                                            <div
                                                style={{
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    backgroundColor: legend.color,
                                                    marginRight: '8px',
                                                }}
                                            />
                                            <span className="fw-medium" style={{ width: '100px' }}>{legend.name}</span>
                                        </div>
                                        <div className="fw-bold" style={{ fontSize: '1.1rem' }}>
                                            {legend.value.toLocaleString()}
                                        </div>
                                        <div
                                            className={legend.change >= 0 ? 'text-success' : 'text-danger'}
                                        >
                                            {legend.change >= 0 ? '+' : ''}
                                            {legend.change}%
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Total and Overall Change */}
                            <div className="text-center">
                                <div className="fw-bold" style={{ fontSize: '1.2rem' }}>
                                    Total Customers: {customerStats.total.toLocaleString()}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Registration Trend & Checkout Completion */}
            <Row>
                {/* Registration Trend with filters and toggle - 2/3 width */}
                <Col xs={12} md={8} className="mb-3">
                    <Card className="shadow border-0">
                        <Card.Body>
                            <Card.Title>Registration Trend</Card.Title>
                            <div className="text-muted mb-3">Registrations over time</div>
                            <div className="mb-3 d-flex gap-2">
                                <div className="btn-group me-2">
                                    {['hourly', 'daily', 'monthly'].map((filter) => (
                                        <button
                                            key={filter}
                                            className={`btn btn-${registrationFilter === filter ? 'primary' : 'outline-primary'}`}
                                            onClick={() => setRegistrationFilter(filter as any)}
                                        >
                                            {filter.charAt(0).toUpperCase() + filter.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                <div className="btn-group">
                                    {['actual', 'trend'].map((type) => (
                                        <button
                                            key={type}
                                            className={`btn btn-${registrationTrendToggle === type ? 'primary' : 'outline-primary'}`}
                                            onClick={() => setRegistrationTrendToggle(type as 'actual' | 'trend')}
                                        >
                                            {type === 'trend' ? 'Trend' : 'Actual'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {registrationsLoading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={registrationsData?.[registrationFilter] || []}>
                                        <defs>
                                            <linearGradient id="registrationGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={colors[1]} stopOpacity={0.8} />
                                                <stop offset="100%" stopColor={colors[1]} stopOpacity={0.3} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="registrations" fill="url(#registrationGradient)" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Checkout Completion Pie - 1/3 width */}
                <Col xs={12} md={4}>
                    <Card className="shadow border-0">
                        <Card.Body>
                            <Card.Title>Checkout Completion
                                <span className="text-muted" style={{ fontSize: '0.9rem', marginLeft: '10px' }}>
                                    {effectiveDate.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}
                                </span>
                            </Card.Title>                            
                            <div className="text-muted mb-3">Completed vs Not Completed this month</div>
                            {checkoutLoading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: 250 }}>
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: 'Completed', value: checkoutData.completed },
                                                { name: 'Not Completed', value: checkoutData.incomplete }
                                            ]}
                                            dataKey="value"
                                            innerRadius={60}
                                            outerRadius={80}
                                            labelLine={false}
                                        >
                                            <Cell fill={colors[1]} />
                                            <Cell fill={colors[0]} />
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                            {/* Legends with totals */}
                            <div className="d-flex justify-content-between mb-3">
                                {[{
                                    name: 'Completed',
                                    color: colors[1],
                                    value: checkoutData.completed,
                                    change: checkoutData.completedChange,
                                }, {
                                    name: 'Not Completed',
                                    color: colors[0],
                                    value: checkoutData.incomplete,
                                    change: checkoutData.incompleteChange,
                                }].map((legend, i) => (
                                    <div
                                        key={i}
                                        className="px-3 py-2 text-center"
                                        style={{
                                            backgroundColor: '#F3F4F6',
                                            borderRadius: '0.5rem',
                                            width: '48%',
                                        }}
                                    >
                                        <div className="d-flex justify-content-center align-items-center mb-1">
                                            <div
                                                style={{
                                                    width: '10px',
                                                    height: '10px',
                                                    borderRadius: '50%',
                                                    backgroundColor: legend.color,
                                                    marginRight: '8px',
                                                }}
                                            />
                                            <span className="fw-medium" style={{ fontSize: '0.95rem', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.1' }}>{legend.name}</span>
                                        </div>
                                        <div className="fw-bold" style={{ fontSize: '1.1rem' }}>
                                            {legend.value.toLocaleString()}
                                        </div>
                                        <div className={legend.change >= 0 ? 'text-success' : 'text-danger'}>
                                            {legend.change >= 0 ? '+' : ''}{legend.change}% vs last month
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default SiteAnalytics;
